import React from "react";
import { Button } from "../../atoms/Button/index.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { cn } from "../../lib/utils.js";

/* ---------------- ToggleGroup ----------------
   A row of toggle buttons with at most one pressed. Each option is a Button
   with `pressed`, so the part has no colour map of its own: at rest an
   option is Button secondary with no tone; a pressed option with no tone
   takes Button's teal selection; a pressed option with a tone is Button soft
   with that tone, whose fill comes from lib/tones.ts.

   Generic over the option value <T>. React.forwardRef cannot keep a generic
   type parameter without a lossy cast, so this part has no forwardRef, the
   same carve-out as SegmentedControl. */

/** The Button size of every option: 24/28/36/44px tall. */
export type ToggleGroupSize = "xs" | "sm" | "md" | "lg";

interface ToggleGroupOptionBase<T extends string | number> {
  value: T;
  /** The pressed paint: Button soft with this tone. Omitted, the pressed
   *  option takes the teal selection. An option at rest shows no tone. */
  tone?: StatusTone | undefined;
  /**
   * Why the option cannot be chosen. Its presence disables the option and
   * shows the reason as visible muted text under the group, linked by the
   * option's `aria-describedby`. It is never a native `title`.
   */
  disabledReason?: string | undefined;
}

interface ToggleGroupTextOption<
  T extends string | number,
> extends ToggleGroupOptionBase<T> {
  label: string;
  /** A decorative glyph before the label (aria-hidden). */
  icon?: React.ReactNode;
  iconOnly?: false | undefined;
}

/** A square option that shows only its icon. `label` is its accessible
 *  name (`aria-label`). */
interface ToggleGroupIconOnlyOption<
  T extends string | number,
> extends ToggleGroupOptionBase<T> {
  label: string;
  icon: Exclude<React.ReactNode, null | undefined | boolean>;
  iconOnly: true;
}

export type ToggleGroupOption<T extends string | number = string> =
  | ToggleGroupTextOption<T>
  | ToggleGroupIconOnlyOption<T>;

/**
 * `role` is omitted so a caller cannot replace `group`. `onChange` and
 * `defaultValue` are omitted because the DOM's versions collide with the
 * typed ones below, and `children` because the options are the content.
 */
export interface ToggleGroupProps<
  T extends string | number = string,
> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange" | "defaultValue" | "role" | "aria-label" | "children"
> {
  options: readonly ToggleGroupOption<T>[];
  /** The pressed option's value, or `null` when none is pressed. */
  value: T | null;
  /** Called with the chosen value, or with `null` when a `clearable` group's
   *  pressed option is activated again. */
  onChange: (value: T | null) => void;
  /** Activating the pressed option releases it (`onChange(null)`). Without
   *  it, activating the pressed option changes nothing. Default `false`. */
  clearable?: boolean | undefined;
  /** Button size of every option. Default `md`. */
  size?: ToggleGroupSize | undefined;
  /** The group's accessible name. Required: a row of pressed buttons means
   *  nothing without it. */
  "aria-label": string;
}

/**
 * A single-select value picker: one pressed toggle button at most, and
 * optionally none (`clearable`).
 *
 * Which part to pick. Single-select value pickers use ToggleGroup;
 * independent on/off chips use Chip; views use SegmentedControl; forms use
 * RadioGroup.
 */
export function ToggleGroup<T extends string | number = string>({
  options,
  value,
  onChange,
  clearable = false,
  size = "md",
  className,
  ...rest
}: ToggleGroupProps<T>): JSX.Element {
  /* A `disabledReason` is visible text under the group. Only then does a
     wrapper exist, and it takes `className`, the SegmentedControl shape. */
  const reasonId = React.useId();
  const hasReasons = options.some((o) => o.disabledReason !== undefined);
  const group = (
    <div
      // Spread first, so `role` below cannot be replaced at runtime.
      {...rest}
      role="group"
      className={cn(
        "inline-flex max-w-full flex-wrap gap-1.5",
        !hasReasons && className,
      )}
    >
      {options.map((o, index) => {
        const pressed = o.value === value;
        const hasReason = o.disabledReason !== undefined;
        const paint =
          pressed && o.tone
            ? ({ variant: "soft", tone: o.tone } as const)
            : ({ variant: "secondary" } as const);
        const face = o.iconOnly
          ? ({ iconOnly: true, "aria-label": o.label } as const)
          : {};
        return (
          <Button
            key={String(o.value)}
            {...paint}
            {...face}
            size={size}
            pressed={pressed}
            disabled={hasReason}
            aria-describedby={hasReason ? `${reasonId}-${index}` : undefined}
            onClick={() => {
              if (!pressed) onChange(o.value);
              else if (clearable) onChange(null);
            }}
          >
            {o.icon ? (
              <span aria-hidden="true" className="inline-flex shrink-0">
                {o.icon}
              </span>
            ) : null}
            {o.iconOnly ? null : o.label}
          </Button>
        );
      })}
    </div>
  );
  if (!hasReasons) return group;
  return (
    <div
      className={cn(
        "inline-flex max-w-full flex-col items-start gap-1",
        className,
      )}
    >
      {group}
      {options.map((o, index) =>
        o.disabledReason === undefined ? null : (
          <p
            key={String(o.value)}
            id={`${reasonId}-${index}`}
            className={cn("text-dense-1", TONES.neutral.text)}
          >
            {o.disabledReason}
          </p>
        ),
      )}
    </div>
  );
}
